import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  Briefcase, 
  GraduationCap, 
  Package, 
  MessageSquare, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Handshake,
  FileDown,
  Megaphone,
  Monitor,
  Image,
  BookOpen
} from 'lucide-react';
import { logout, getUser } from '@/lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_steward-health-hub/artifacts/1if4pbt1_steward.png";

const sidebarLinks = [
  { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
  { name: 'Leads', path: '/admin/leads', icon: Users },
  { name: 'Services', path: '/admin/services', icon: Briefcase },
  { name: 'Courses', path: '/admin/courses', icon: GraduationCap },
  { name: 'Products', path: '/admin/products', icon: Package },
  { name: 'Testimonials', path: '/admin/testimonials', icon: MessageSquare },
  { name: 'Blog Posts', path: '/admin/blogs', icon: FileText },
  { name: 'Clients & Partners', path: '/admin/partners', icon: Handshake },
  { name: 'Brochures', path: '/admin/brochures', icon: FileDown },
  { name: 'Announcements', path: '/admin/announcements', icon: Megaphone },
  { name: 'EMR/HIS Plans', path: '/admin/emr-plans', icon: Monitor },
  { name: 'Training Gallery', path: '/admin/training-gallery', icon: Image },
  { name: 'Academic Partners', path: '/admin/academic-partners', icon: BookOpen },
  { name: 'Settings', path: '/admin/settings', icon: Settings },
];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-gray-200">
            <Link to="/admin" className="flex items-center">
              <img src={LOGO_URL} alt="Steward Healthcare" className="h-10 object-contain" />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {sidebarLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive 
                      ? 'bg-[#1a2a5e] text-white' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-[#1a2a5e]'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon size={20} />
                  <span>{link.name}</span>
                  {isActive && <ChevronRight size={16} className="ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User info & logout */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#1a2a5e] flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div>
                <p className="font-medium text-sm text-gray-900">{user?.name || 'Admin'}</p>
                <p className="text-xs text-gray-500">{user?.email || 'info@stewardindia.com'}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </Button>
            
            <div className="flex items-center gap-2 ml-auto">
              <Link to="/" target="_blank" className="text-sm text-gray-600 hover:text-[#1a2a5e]">
                View Website →
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
